/* eslint-disable object-property-newline */
import React, { createContext, useContext, useRef, useState, useCallback, useEffect } from 'react';
import useApi from 'api';
import { RatingItemSubCategory, RatingItemDescription } from 'global/const';
import { compareObjects } from 'lib';

const DataContext = createContext({});
export const useDataContext = () => useContext(DataContext);

export const DataProvider = ({ children }) => {
    const init = useRef({});
    /* staff */
    const [staffs, setStaffs] = useState([]);
    const getStaffReq = useApi('getStaff');
    const getStaffs = useCallback(() => {
        getStaffReq({ loading: false }).then((res) => {
            if (res.staffs) {
                const data = res.staffs
                    .map((item) => ({
                        ...item,
                        value: item.id,
                        label: item.userName
                    }))
                    .sort(compareObjects('label'));
                setStaffs(data);
            }
        });
    }, [getStaffReq]);
    /* company */
    const [companies, setCompanies] = useState([]);
    const getCompaniesReq = useApi('getCompanies');
    const getCompanies = useCallback(() => {
        getCompaniesReq({ loading: false }).then((res) => {
            if (res.companyVos) {
                const data = res.companyVos
                    .map((item) => ({
                        ...item,
                        value: item.companyId,
                        label: item.companyName
                    }))
                    .sort(compareObjects('label'));
                setCompanies(data);
            }
        });
    }, [getCompaniesReq]);
    /* region */
    const [regions, setRegions] = useState([]);
    const [districtRegions, setDistrictRegions] = useState([]);
    const getRegionsReq = useApi('getRegions');
    const getRegions = useCallback(() => {
        getRegionsReq({ loading: false }).then((res) => {
            if (res.regions) {
                const data = res.regions.map((item) => ({
                    ...item,
                    value: item.id,
                    label: item.name,
                    children: item.children.map((x) => ({
                        ...x,
                        value: x.id,
                        label: x.name
                    }))
                }));
                setRegions(data);
                const districtRegionOptions = data.reduce((prev, curr) => {
                    const options = curr.children.map((item) => ({ ...item, parentId: curr.id }));
                    return [...prev, ...options];
                }, []);
                setDistrictRegions(districtRegionOptions);
            }
        });
    }, [getRegionsReq]);
    /* member rating item */
    const [ratingItems, setRatingItems] = useState([]);
    const getRatingItemsReq = useApi('getRatingItems');
    const getRatingItems = useCallback(() => {
        setRatingItems([]);
        getRatingItemsReq({ loading: false }).then((res) => {
            if (res.items) {
                const data = res.items.map((item) => ({
                    ...item,
                    scoreChange: Math.abs(item.scoreChange),
                    value: item.itemId,
                    label: (
                        RatingItemSubCategory[item.subCategory] ?? ''
                    ) + (
                        RatingItemDescription[item.item] ?
                            ` - ${RatingItemDescription[item.item]}` : ''
                    )
                }));
                setRatingItems(data);
            }
        });
    }, [getRatingItemsReq]);
    /* station */
    const [stations, setStations] = useState([]);
    const getStationsReq = useApi('getStations');
    const getStations = useCallback(() => {
        getStationsReq({ loading: false }).then((res) => {
            if (res.stations) {
                const data = res.stations
                    .map((item) => ({
                        ...item,
                        value: item.id,
                        label: item.name
                    }))
                    .sort(compareObjects('label'));
                setStations(data);
            }
        });
    }, [getStationsReq]);
    /* station group */
    const [stationGroup, setStationGroup] = useState([]);
    const getStationGroupReq = useApi('getStationGroup');
    const getStationGroup = useCallback(() => {
        getStationGroupReq({ loading: false }).then((res) => {
            if (res.stationGroups) {
                const data = res.stationGroups;
                setStationGroup(data);
            }
        });
    }, [getStationGroupReq]);
    /* station owner */
    const [stationOwners, setStationOwners] = useState([]);
    const getStationOwnersReq = useApi('getStationOwners');
    const getStationOwners = useCallback(() => {
        getStationOwnersReq({ loading: false }).then((res) => {
            if (res.owners) {
                const data = res.owners
                    .map((item) => ({
                        ...item,
                        value: item.ownerId,
                        label: item.ownerName
                    }))
                    .sort(compareObjects('label'));
                setStationOwners(data);
            }
        });
    }, [getStationOwnersReq]);
    /* car model */
    const [carModels, setCarModels] = useState([]);
    const getCarModelsReq = useApi('getCarModelAll');
    const getCarModels = useCallback(() => {
        getCarModelsReq({ loading: false }).then((res) => {
            if (res.carModels) {
                const data = res.carModels
                    .map((item) => ({
                        ...item,
                        value: item.modelId,
                        label: item.name
                    }))
                    .sort(compareObjects('label'));
                setCarModels([
                    { value: 'all', label: '全部車款' },
                    ...data
                ]);
            }
        });
    }, [getCarModelsReq]);
    /* car mileage */
    const [carMileages, setCarMileages] = useState([]);
    const getCarMileagesReq = useApi('getCarMileages');
    const getCarMileages = useCallback(() => {
        getCarMileagesReq({ loading: false }).then((res) => {
            if (res.carMileageClasses) {
                const data = res.carMileageClasses.map((item) => ({
                    ...item,
                    value: item.className,
                    label: item.className
                }));
                setCarMileages(data);
            }
        });
    }, [getCarMileagesReq]);
    /* car insurance */
    const [carInsurances, setCarInsurances] = useState([]);
    const getCarInsurancesReq = useApi('getCarInsurances');
    const getCarInsurances = useCallback(() => {
        getCarInsurancesReq({ loading: false }).then((res) => {
            if (res.insurances) {
                const data = res.insurances
                    .map((item) => ({
                        ...item,
                        value: item.insuranceId,
                        label: item.name
                    }))
                    .sort(compareObjects('label'));
                setCarInsurances(data);
            }
        });
    }, [getCarInsurancesReq]);
    /* extra fee */
    const [extraFees, setExtraFees] = useState([]);
    const getExtraFeesReq = useApi('getExtraFees');
    const getExtraFees = useCallback(() => {
        getExtraFeesReq({ loading: false }).then((res) => {
            if (res.extraFees) {
                const data = res.extraFees.map((item) => ({
                    ...item,
                    value: item.extraFeesId,
                    label: item.name
                }));
                setExtraFees(data);
            }
        });
    }, [getExtraFeesReq]);
    /* car */
    const [cars, setCars] = useState([]);
    const getCarsReq = useApi('getCars');
    const getCars = useCallback(() => {
        getCarsReq({ loading: false }).then((res) => {
            if (res.carVos) {
                const data = res.carVos
                    .map((item) => ({
                        ...item,
                        value: item.carId,
                        label: item.plateNumber
                    }))
                    .sort(compareObjects('label'));
                setCars(data);
            }
        });
    }, [getCarsReq]);
    /* technician group */
    const [technicianGroups, setTechnicianGroups] = useState([]);
    const getTechnicianGroupsReq = useApi('getTechnicianGroups');
    const getTechnicianGroups = useCallback(() => {
        getTechnicianGroupsReq({ loading: false }).then((res) => {
            if (res.technicianGroupVos) {
                const data = res.technicianGroupVos
                    .map((item) => ({
                        ...item,
                        technicians: item.technicianRelationVos?.map((x) => x.technicianId),
                        value: item.technicianGroupId,
                        label: item.technicianGroupName
                    }))
                    .sort(compareObjects('label'));
                setTechnicianGroups(data);
            }
        });
    }, [getTechnicianGroupsReq]);
    /* technician */
    const [technicians, setTechnicians] = useState([]);
    const getTechniciansReq = useApi('getTechnicians');
    const getTechnicians = useCallback(() => {
        getTechniciansReq({ loading: false }).then((res) => {
            if (res.technicianVos) {
                const data = res.technicianVos
                    .map((item) => ({
                        ...item,
                        value: item.technicianId,
                        label: `${item.companyName ? `${item.companyName} - ` : ''}${item.technicianName}`
                    }))
                    .sort(compareObjects('label'));
                setTechnicians(data);
            }
        });
    }, [getTechniciansReq]);

    return (
        <DataContext.Provider
            value={{
                init,
                staffs, setStaffs, getStaffs,
                companies, setCompanies, getCompanies,
                regions, districtRegions, setRegions, getRegions,
                ratingItems, setRatingItems, getRatingItems,
                stations, setStations, getStations,
                stationGroup, setStationGroup, getStationGroup,
                stationOwners, setStationOwners, getStationOwners,
                carModels, setCarModels, getCarModels,
                carMileages, setCarMileages, getCarMileages,
                carInsurances, setCarInsurances, getCarInsurances,
                extraFees, setExtraFees, getExtraFees,
                cars, setCars, getCars,
                technicianGroups, setTechnicianGroups, getTechnicianGroups,
                technicians, setTechnicians, getTechnicians
            }}
        >
            {children}
        </DataContext.Provider>
    );
};

export const useDataOptions = (name, { forceUpdate = true } = {}) => {
    const {
        init,
        [name]: options,
        [`set${name[0].toUpperCase()}${name.slice(1)}`]: setOptions,
        [`get${name[0].toUpperCase()}${name.slice(1)}`]: getOptions
    } = useDataContext();

    useEffect(() => {
        if (init.current[name] && forceUpdate) {
            getOptions();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceUpdate]);

    useEffect(() => {
        if (!init.current[name]) {
            init.current[name] = true;
            getOptions();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        options,
        setOptions,
        getOptions
    };
};
